import { Injectable, inject } from '@angular/core';
import { PermanentTag } from '@enrollhere/classes';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import { getLanguageLabel } from '../data/language';

export enum PermanentTagQueryKey {
  AGENCY_TAGS = 'permanentTags-agency',
}

interface TagsResponse {
  tags?: unknown[];
}

const TAG_ID_SEPARATOR = '#-#';

/**
 * Local id after `agencyId#-#tagId`. Tasks and memberships often store the
 * composite form while `GET /agency/:id/tags` keys `byId` with the suffix.
 */
export function permanentTagSuffix(tagId: string): string | undefined {
  const separatorAt = tagId.indexOf(TAG_ID_SEPARATOR);
  if (separatorAt < 0) {
    return undefined;
  }
  const suffix = tagId.slice(separatorAt + TAG_ID_SEPARATOR.length);
  return suffix || undefined;
}

/** True when two stored tag ids refer to the same permanent tag. */
export function permanentTagIdsMatch(left: string, right: string): boolean {
  if (left === right) {
    return true;
  }
  const leftSuffix = permanentTagSuffix(left);
  const rightSuffix = permanentTagSuffix(right);
  if (leftSuffix && leftSuffix === right) {
    return true;
  }
  if (rightSuffix && rightSuffix === left) {
    return true;
  }
  if (leftSuffix && rightSuffix && leftSuffix === rightSuffix) {
    return true;
  }
  return false;
}

function catalogTagName(
  tagId: string,
  catalogById: ReadonlyMap<string, PermanentTag>
): string | undefined {
  const exact = catalogById.get(tagId)?.name;
  if (exact) {
    return exact;
  }
  const suffix = permanentTagSuffix(tagId);
  return suffix ? catalogById.get(suffix)?.name : undefined;
}

/**
 * Display name for a permanent tag id.
 *
 * `GET /agency/:id/tags` deliberately omits built-in tags (language, product
 * line, the default agent tags) and archived ones, so the agency catalog alone
 * leaves ids like `LANG_EN` unresolved — hence the language fallback. Anything
 * still unknown renders as its raw id rather than being hidden: a tag that is
 * genuinely applied should stay visible even if we can't name it.
 */
export function resolveTagLabel(
  tagId: string,
  catalogById: ReadonlyMap<string, PermanentTag>
): string {
  const fromCatalog = catalogTagName(tagId, catalogById);
  if (fromCatalog) {
    return fromCatalog;
  }
  return getLanguageLabel(tagId) ?? tagId;
}

/** Indexes a catalog for `resolveTagLabel`, including `agencyId#-#id` suffixes. */
export function indexTagsById(
  tags: readonly PermanentTag[]
): Map<string, PermanentTag> {
  const catalog = new Map<string, PermanentTag>();
  for (const tag of tags) {
    if (!tag.id) {
      continue;
    }
    catalog.set(tag.id, tag);
    const suffix = permanentTagSuffix(tag.id);
    if (suffix && !catalog.has(suffix)) {
      catalog.set(suffix, tag);
    }
  }
  return catalog;
}

/** Name map for grid/cell lookups; keys include both full and suffix ids. */
export function tagNamesFromCatalog(
  tags: readonly PermanentTag[]
): Record<string, string> {
  const names: Record<string, string> = {};
  for (const [id, tag] of indexTagsById(tags)) {
    names[id] = tag.name || id;
  }
  return names;
}

/** Resolve a stored assignee tag id against a `tagNamesFromCatalog` map. */
export function lookupTagName(
  tagId: string,
  names: Record<string, string> | undefined
): string {
  if (!names) {
    return getLanguageLabel(tagId) ?? tagId;
  }
  const exact = names[tagId];
  if (exact) {
    return exact;
  }
  const suffix = permanentTagSuffix(tagId);
  if (suffix) {
    const fromSuffix = names[suffix];
    if (fromSuffix) {
      return fromSuffix;
    }
  }
  return getLanguageLabel(tagId) ?? tagId;
}

@Injectable({ providedIn: 'root' })
export class PermanentTagService {
  private readonly api = inject(EnrollhereApiService);

  getAgencyTagsQueryOptions(agencyId: string) {
    return {
      queryKey: [PermanentTagQueryKey.AGENCY_TAGS, agencyId] as const,
      queryFn: () => this.getAgencyTags(agencyId),
      enabled: !!agencyId,
      staleTime: 5 * 60 * 1000,
    };
  }

  async getAgencyTags(agencyId: string): Promise<PermanentTag[]> {
    const res = await this.api.get<TagsResponse>(
      `/agency/${encodeURIComponent(agencyId)}/tags`
    );
    return (res?.tags ?? []).map(raw => PermanentTag.fromJSON(raw));
  }

  /**
   * Soft-delete (archive) a permanent tag. The backend strips the tag from
   * every user and queue that references it and marks the definition archived,
   * so historical calls/policies still resolve its name. Archived tags are
   * filtered out everywhere they're listed.
   */
  async archiveTag(agencyId: string, tagId: string): Promise<void> {
    await this.api.delete(
      `/agency/${encodeURIComponent(agencyId)}/tags/${encodeURIComponent(tagId)}`
    );
  }
}
